/**
 * Sheet "Báo sai thông tin" — điều tra viên đề xuất sửa Hộ / Vườn / MM ngay tại thực địa
 * (Doc/11a "Xác minh hồ sơ và đề xuất sửa", E2, E3). Mở được từ hồ sơ và từ thẻ chủ thể
 * đầu phiếu — cùng một sheet, đóng lại là về đúng chỗ đang làm.
 *
 * Dữ liệu chủ KHÔNG đổi khi gửi (D1): hồ sơ vẫn hiện giá trị hiện hành, kèm nhãn
 * "Đề xuất: … · chờ duyệt". Mất mạng thì đề xuất vào hàng đợi trên máy và tự gửi khi có
 * mạng (Q4); `clientRequestId` giữ cho lần gửi lại không tạo đề xuất thứ hai.
 *
 * Chưa có ở đây: nút "Đúng" để xác minh từng trường (E1) — cần `POST …/:id/verify` của
 * phase 2 backend (Doc/11 mục 4). Tách thửa cũng chờ `action: split`.
 */
import * as api from '../api-client.js';
import { enqueue, listQueuedChangeRequests } from '../offline/queue.js';
import { el, esc, toast } from './util.js';
import { gpsFieldHtml, attachGpsCapture, currentPositionOrNull } from './gps-capture.js';
import {
  PROPOSAL_FIELDS,
  REASONS,
  ENTITY_LABEL,
  buildProposalItems,
  displayValue,
  fieldLabel as fieldLabelOf,
  pendingByField,
  hasPendingDeactivate,
  newClientRequestId,
  shortId,
} from './proposal-fields.js';

const DEACTIVATE_LABEL = {
  household: 'Hộ đã chuyển đi / không còn canh tác',
  plot: 'Vườn không còn canh tác / đã bán',
  middleman: 'Đại lý ngừng hoạt động',
};

/** Đề xuất còn chờ của các bản ghi này: trên máy chủ (`pending`) + trong hàng đợi (`queued`). */
export async function loadOpenProposals(entityIds) {
  const ids = [...new Set(entityIds.filter(Boolean))];
  const [server, queued] = await Promise.all([
    Promise.all(
      ids.map((entityId) =>
        api.changeRequests
          .list({ entityId, status: 'pending', pageSize: 50 })
          .then((r) => r.data || [])
          // Mất mạng hay lỗi đọc: hồ sơ vẫn phải mở được, chỉ thiếu nhãn "chờ duyệt".
          .catch(() => []),
      ),
    ),
    listQueuedChangeRequests().catch(() => []),
  ]);
  return [
    ...server.flat(),
    ...queued.filter((q) => ids.includes(q.entityId)).map((q) => ({ ...q, id: null, status: 'queued' })),
  ];
}

function isTransient(err) {
  if (err instanceof TypeError) return true;
  if (err?.name === 'AbortError') return true;
  if (err?.status === 429) return true;
  return err?.status === undefined || err.status >= 500;
}

/**
 * Gửi một đề xuất; không gửi được vì mạng thì xếp hàng. Lỗi nghiệp vụ (422, 409) ném ra
 * để sheet hiện ngay — xếp hàng chúng chỉ để chúng hỏng lần nữa ở bảng đồng bộ.
 *
 * @returns {Promise<{queued: true} | {queued: false, changeRequest: object}>}
 */
async function sendProposal(payload, link) {
  if (navigator.onLine === false) {
    await enqueue({ kind: 'changeRequest', payload, link });
    return { queued: true };
  }
  try {
    return { queued: false, changeRequest: await api.changeRequests.create(payload) };
  } catch (err) {
    if (!isTransient(err)) throw err;
    await enqueue({ kind: 'changeRequest', payload, link });
    return { queued: true };
  }
}

function pendingChipHtml(pending, entity, field) {
  if (!pending) return '';
  const shown = displayValue(entity, field, pending.value) ?? '(xoá trống)';
  return `<span class="pending-chip">Đã có đề xuất: <b>${esc(shown)}</b> · ${pending.status === 'queued' ? 'chờ mạng' : 'chờ duyệt'}</span>`;
}

function editorHtml(entity, field, record) {
  if (field.kind === 'gps') {
    return gpsFieldHtml({ label: 'Toạ độ đúng', latValue: record.gpsLat ?? '', lngValue: record.gpsLng ?? '' });
  }
  const id = `cr-${field.key}`;
  if (field.kind === 'enum') {
    return `<div class="form-row"><label for="${id}">Giá trị đúng</label>
      <select id="${id}" name="${field.key}">
        <option value="">— Chọn —</option>
        ${field.options.map(([v, l]) => `<option value="${esc(v)}" ${String(record[field.key] ?? '') === v ? 'selected' : ''}>${esc(l)}</option>`).join('')}
      </select></div>`;
  }
  return `<div class="form-row"><label for="${id}">Giá trị đúng</label>
    <input id="${id}" name="${field.key}" value="${esc(record[field.key] ?? '')}" ${field.inputmode ? `inputmode="${field.inputmode}"` : ''} autocomplete="off"></div>`;
}

function rowHtml(entity, field, record, pending) {
  const fields = field.kind === 'gps' ? ['gpsLat', 'gpsLng'] : [field.key];
  const current =
    field.kind === 'gps'
      ? record.gpsLat != null && record.gpsLng != null ? `${record.gpsLat}, ${record.gpsLng}` : null
      : displayValue(entity, field.key, record[field.key]);
  const blocked = fields.map((f) => pending.get(f)).find(Boolean);
  return `
    <div class="vrow" data-key="${esc(field.key)}">
      <div class="vrow-label">${esc(field.label)}${field.audit ? '<span class="vrow-tag">dùng tính chỉ số</span>' : ''}</div>
      <div class="vrow-old${current ? '' : ' is-empty'}">${current ? esc(current) : '<span class="hint-text">Chưa có</span>'}</div>
      ${fields.map((f) => pendingChipHtml(pending.get(f), entity, f)).join('')}
      <button type="button" class="vrow-toggle" data-act="toggle" aria-pressed="false" ${blocked ? 'disabled title="Chờ người duyệt xử lý đề xuất đang có trước"' : ''}>
        <span class="ms ms-sm">edit</span>Khác thực tế
      </button>
      <div class="vrow-edit" hidden>
        ${editorHtml(entity, field, record)}
        <div class="reason-chips" role="group" aria-label="Lý do sửa ${esc(field.label)}">
          ${REASONS.map(([code, label]) => `<button type="button" class="reason-chip" data-reason="${code}" aria-pressed="false">${esc(label)}</button>`).join('')}
        </div>
        <input class="reason-note" name="reasonNote-${esc(field.key)}" placeholder="Ghi chú lý do (bắt buộc khi chọn Khác)" hidden>
      </div>
    </div>`;
}

/**
 * @param {object} opts
 * @param {'household'|'plot'|'middleman'} opts.entity
 * @param {object} opts.record     bản ghi hiện hành (cần `id`)
 * @param {string} opts.title      "Nguyễn Văn A" / "Vườn Eatan.1"
 * @param {string} [opts.link]     hash mở lại hồ sơ — cho bảng đồng bộ khi đề xuất bị kẹt
 * @param {object[]} [opts.openProposals]  kết quả `loadOpenProposals`, nếu nơi gọi đã nạp
 * @param {(r: {queued: boolean, items: object[], action: string}) => void} [opts.onSent]
 */
export async function openProposalSheet({ entity, record, title, link, openProposals, onSent }) {
  const fields = PROPOSAL_FIELDS[entity];
  const open = openProposals ?? (await loadOpenProposals([record.id]));
  const mine = open.filter((cr) => cr.entityId === record.id);
  const pending = pendingByField(mine);
  const deactivateBlocked = hasPendingDeactivate(mine);

  const backdrop = el('div', 'dialog-backdrop proposal-backdrop');
  const form = el('form', 'dialog proposal-sheet');
  form.noValidate = true;
  form.setAttribute('role', 'dialog');
  form.setAttribute('aria-modal', 'true');
  form.setAttribute('aria-label', 'Báo sai thông tin');
  form.innerHTML = `
    <div class="dialog-head">
      <div class="proposal-head">
        <h3>Báo sai thông tin</h3>
        <span class="hint-text">${esc(ENTITY_LABEL[entity])} · ${esc(title)}</span>
      </div>
      <button class="icon-btn" type="button" data-act="close" aria-label="Đóng"><span class="ms">close</span></button>
    </div>
    <div class="dialog-body">
      <p class="hint-text">Bấm <b>Khác thực tế</b> ở thông tin không đúng, nhập giá trị đúng và chọn lý do.
        Hồ sơ chỉ đổi khi người duyệt đồng ý.${entity === 'plot' ? ' Phiếu nháp tính ngay theo diện tích / thành phần đất mới (Q1).' : ''}</p>
      ${navigator.onLine === false ? `<div class="status-banner tone-warn"><span class="ms">cloud_off</span>
        <span>Đang mất mạng — đề xuất được lưu trên máy và tự gửi khi có mạng.</span></div>` : ''}
      <div class="vrows">${fields.map((f) => rowHtml(entity, f, record, pending)).join('')}</div>
      <div class="form-row proposal-note" hidden>
        <label for="cr-note">Ghi chú cho người duyệt</label>
        <textarea id="cr-note" name="note" rows="2" placeholder="Không bắt buộc. Ví dụ: hộ đã bán 0,4 ha phía suối."></textarea>
      </div>
      <p class="hint-text"><span class="ms ms-sm">my_location</span> Vị trí và thời điểm được tự gắn khi gửi.</p>
      <div class="proposal-deactivate">
        <button type="button" class="btn btn-text danger sm" data-act="deactivate" ${deactivateBlocked ? 'disabled title="Đã có đề xuất ngừng hoạt động đang chờ duyệt"' : ''}>
          <span class="ms ms-sm">block</span>${esc(DEACTIVATE_LABEL[entity])}
        </button>
      </div>
    </div>
    <div class="dialog-error error-text" role="alert" hidden></div>
    <div class="dialog-actions">
      <button class="btn btn-text" type="button" data-act="cancel">Huỷ</button>
      <button class="btn" type="submit" disabled><span class="ms">send</span><span data-submit-label>Gửi đề xuất</span></button>
    </div>`;

  backdrop.appendChild(form);
  document.body.appendChild(backdrop);
  // Đóng sheet là về đúng câu đang nhập (E3): trả focus cho phần tử đã mở sheet.
  const opener = document.activeElement;

  const close = () => {
    backdrop.remove();
    document.removeEventListener('keydown', onKey);
    window.removeEventListener('hashchange', close);
    if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
  };
  const onKey = (e) => {
    if (e.key === 'Escape') close();
  };
  document.addEventListener('keydown', onKey);
  // Gắn vào `document.body`: đổi trang thì phải tự đóng, router không dọn nó.
  window.addEventListener('hashchange', close);
  form.querySelector('[data-act="close"]').addEventListener('click', close);
  form.querySelector('[data-act="cancel"]').addEventListener('click', close);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });
  attachGpsCapture(form);

  /** Trạng thái từng hàng; giá trị nhập đọc thẳng từ ô lúc gửi. */
  const rows = Object.fromEntries(fields.map((f) => [f.key, { diff: false, reason: null }]));
  const errBox = form.querySelector('.dialog-error');
  const submitBtn = form.querySelector('button[type="submit"]');
  const submitLabel = form.querySelector('[data-submit-label]');
  const noteRow = form.querySelector('.proposal-note');

  function refreshSubmit() {
    const n = Object.values(rows).filter((r) => r.diff).length;
    submitBtn.disabled = n === 0;
    submitLabel.textContent = n ? `Gửi đề xuất (${n} trường)` : 'Gửi đề xuất';
    noteRow.hidden = n === 0;
  }

  form.querySelectorAll('.vrow').forEach((rowEl) => {
    const key = rowEl.dataset.key;
    const toggle = rowEl.querySelector('[data-act="toggle"]');
    const edit = rowEl.querySelector('.vrow-edit');
    const noteInput = rowEl.querySelector('.reason-note');
    toggle.addEventListener('click', () => {
      rows[key].diff = !rows[key].diff;
      toggle.setAttribute('aria-pressed', String(rows[key].diff));
      rowEl.classList.toggle('is-diff', rows[key].diff);
      edit.hidden = !rows[key].diff;
      if (rows[key].diff) edit.querySelector('input, select')?.focus();
      refreshSubmit();
    });
    rowEl.querySelectorAll('.reason-chip').forEach((chip) =>
      chip.addEventListener('click', () => {
        rows[key].reason = chip.dataset.reason;
        rowEl.querySelectorAll('.reason-chip').forEach((c) => c.setAttribute('aria-pressed', String(c === chip)));
        noteInput.hidden = rows[key].reason !== 'other';
        if (!noteInput.hidden) noteInput.focus();
      }),
    );
  });

  function readRows() {
    const out = {};
    for (const f of fields) {
      const rowEl = form.querySelector(`.vrow[data-key="${f.key}"]`);
      const base = { ...rows[f.key], note: rowEl.querySelector('.reason-note').value };
      if (f.kind === 'gps') {
        out[f.key] = { ...base, lat: rowEl.querySelector('input[name="gpsLat"]').value, lng: rowEl.querySelector('input[name="gpsLng"]').value };
      } else {
        out[f.key] = { ...base, value: rowEl.querySelector(`[name="${f.key}"]`).value };
      }
    }
    return out;
  }

  async function send(action, items) {
    errBox.hidden = true;
    submitBtn.disabled = true;
    try {
      const gps = await currentPositionOrNull();
      const note = form.querySelector('[name="note"]').value.trim();
      const payload = {
        entity,
        entityId: record.id,
        action,
        items,
        ...(gps ? { gps } : {}),
        ...(note ? { note } : {}),
        clientRequestId: newClientRequestId(),
      };
      const res = await sendProposal(payload, link);
      toast(
        res.queued
          ? 'Đã lưu đề xuất trên máy, sẽ tự gửi khi có mạng.'
          : `Đã gửi đề xuất ${shortId(res.changeRequest?.id)}${items.length ? ` (${items.length} trường)` : ''} cho người duyệt.`,
      );
      close();
      onSent?.({ queued: res.queued, items, action });
    } catch (err) {
      console.error('[proposal-sheet] gửi đề xuất lỗi', err);
      errBox.textContent = err?.message || 'Không gửi được đề xuất.';
      errBox.hidden = false;
      refreshSubmit();
    }
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const { items, error } = buildProposalItems(entity, record, readRows());
    if (error) {
      errBox.textContent = error;
      errBox.hidden = false;
      return;
    }
    void send('update', items);
  });

  form.querySelector('[data-act="deactivate"]').addEventListener('click', () => {
    const ok = window.confirm(
      `Gửi đề xuất: ${DEACTIVATE_LABEL[entity].toLowerCase()}?\n\n` +
        `Người duyệt sẽ quyết định. Trong lúc chờ, hồ sơ và phiếu vẫn dùng bình thường.`,
    );
    if (ok) void send('deactivate', []);
  });

  refreshSubmit();
  return { close };
}

/**
 * Danh sách đề xuất còn mở của một hồ sơ: trường, cũ → mới, ai gửi, trạng thái. Người gửi
 * rút được đề xuất của mình khi còn chờ (E2). Không có đề xuất nào → `host` để trống.
 *
 * @param {HTMLElement} host
 * @param {object[]} proposals  kết quả `loadOpenProposals`
 * @param {{titleOf: (cr: object) => string, onChanged: () => void}} opts
 */
export function renderOpenProposals(host, proposals, { titleOf, onChanged }) {
  if (!proposals.length) {
    host.innerHTML = '';
    host.hidden = true;
    return;
  }
  const me = api.getCurrentUser();
  host.hidden = false;
  host.innerHTML = `
    <h2 class="card-title"><span class="ms">pending_actions</span>Đề xuất sửa đang chờ (${proposals.length})</h2>
    <p class="hint-text">Hồ sơ vẫn hiện giá trị hiện hành cho tới khi người duyệt đồng ý.</p>
    <ul class="proposal-list">${proposals
      .map((cr, i) => {
        const queued = cr.status === 'queued';
        const lines =
          cr.action === 'deactivate'
            ? `<li>${esc(DEACTIVATE_LABEL[cr.entity])}</li>`
            : (cr.items || [])
                .map((it) => {
                  const oldShown = displayValue(cr.entity, it.field, it.oldValue);
                  const newShown = displayValue(cr.entity, it.field, it.newValue) ?? '(xoá trống)';
                  return `<li>${esc(fieldLabelOf(cr.entity, it.field))}: ${
                    queued ? '' : `<s>${esc(oldShown ?? '—')}</s> → `
                  }<b>${esc(newShown)}</b></li>`;
                })
                .join('');
        const canWithdraw = !queued && cr.id && cr.requestedBy === me?.id;
        return `<li class="proposal-item">
          <div class="proposal-item-head">
            <span class="badge ${queued ? 'status-draft' : 'status-submitted'}">${queued ? 'Chờ mạng' : 'Chờ duyệt'}</span>
            <b>${esc(titleOf(cr))}</b>
            ${cr.id ? `<span class="mono hint-text">${esc(shortId(cr.id))}</span>` : ''}
          </div>
          <ul class="proposal-lines">${lines}</ul>
          ${cr.note ? `<p class="hint-text">Ghi chú: ${esc(cr.note)}</p>` : ''}
          ${canWithdraw ? `<button type="button" class="btn btn-text sm" data-act="withdraw" data-idx="${i}">Rút đề xuất</button>` : ''}
        </li>`;
      })
      .join('')}</ul>`;

  host.querySelectorAll('[data-act="withdraw"]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      const cr = proposals[Number(btn.dataset.idx)];
      if (!window.confirm('Rút đề xuất này? Người duyệt sẽ không còn thấy nó trong hàng chờ.')) return;
      btn.disabled = true;
      try {
        await api.changeRequests.withdraw(cr.id);
        toast('Đã rút đề xuất');
        onChanged();
      } catch (err) {
        toast('Không rút được: ' + err.message);
        btn.disabled = false;
      }
    }),
  );
}
